import React, { useState } from 'react';
import {
  Box, VStack, HStack, Text, Heading, Badge, Button,
  Divider, Container, Flex, Icon, Alert, AlertIcon,
  Grid, GridItem, SimpleGrid,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FaCreditCard, FaCheckCircle, FaLock, FaVial, FaStethoscope, FaSyringe, FaRegCreditCard, FaTimes } from 'react-icons/fa';

const mockInvoices = [
  {
    invoice_number: 'YW5D',
    payment_status: 'unpaid',
    created_at: '2026-03-18T00:00:00Z',
    lab_name: 'EPIC LABORATORY SERVICES',
    lab_email: 'billing@epiclab.com',
    batch_number: 'BATCH-2026-027',
    total_amount: 60,
    ship_fee: 0,
    line_items: [
      { patient_name: 'John Smith', patient_dob: '1985-06-15', collection_date: '2026-03-17', draw_fee: 15, admin_fee: 5, line_total: 20, doctor_name: 'Dr. Robert Chen', phlebotomist_name: 'Dawn Mobile Phlebotomy', accession: '1042', diagnosis: 'Z00.00; E11.9', tests: ['CBC', 'CMP', 'HbA1c'] },
      { patient_name: 'Maria Garcia', patient_dob: '1990-02-28', collection_date: '2026-03-17', draw_fee: 15, admin_fee: 5, line_total: 20, doctor_name: 'Dr. Sarah Kim', phlebotomist_name: 'Dawn Mobile Phlebotomy', accession: '1043', diagnosis: 'R73.09', tests: ['Glucose', 'Lipid Panel'] },
      { patient_name: 'James Wilson', patient_dob: '1978-11-03', collection_date: '2026-03-17', draw_fee: 15, admin_fee: 5, line_total: 20, doctor_name: 'Dr. Robert Chen', phlebotomist_name: 'Dawn Mobile Phlebotomy', accession: '1044', diagnosis: 'E78.5', tests: ['Lipid Panel', 'TSH'] },
    ],
  },
];

const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;
const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function PublicInvoicePreview() {
  const [paymentTab, setPaymentTab] = useState('card');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const invoices = mockInvoices;
  const allPaid = invoices.every(inv => inv.payment_status === 'paid');
  const grandTotal = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const isMultiple = invoices.length > 1;
  const labName = invoices[0]?.lab_name || 'Client';
  const labEmail = invoices[0]?.lab_email || null;
  const totalLineItems = invoices.reduce((sum, inv) => sum + (inv.line_items?.length || 0), 0);

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 4, md: 8 }}>
      <Container maxW="800px" px={{ base: 3, md: 6 }}>
        <Alert status="info" borderRadius="lg" mb={4} boxShadow="sm">
          <AlertIcon />
          <Text fontSize="sm">This is a <strong>preview</strong> with mock data. The real page will load from the database.</Text>
        </Alert>

        {/* Main Invoice Card */}
        <Box bg="white" borderRadius="2xl" boxShadow="xl" overflow="hidden" border="1px solid" borderColor="gray.100">

          {/* Header */}
          <Box
            bgGradient="linear(to-r, #276749, #38a169)"
            color="white"
            px={{ base: 6, md: 10 }}
            py={8}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              opacity={0.05}
              bgImage="radial-gradient(circle at 25% 25%, white 1px, transparent 1px)"
              bgSize="20px 20px"
            />
            <Flex justify="space-between" align="flex-start" position="relative">
              <VStack align="start" spacing={2}>
                <Text fontSize="lg" fontWeight="800" letterSpacing="0.5px">
                  AtHomeBloodDraw.com
                </Text>
                <Heading size="xl" fontWeight="800" letterSpacing="-0.5px">
                  {isMultiple ? 'INVOICES' : 'INVOICE'}
                </Heading>
                <Text fontSize="sm" opacity={0.7}>
                  Mobile Phlebotomy Services
                </Text>
              </VStack>
              <VStack align="end" spacing={2} mt={1}>
                {isMultiple ? (
                  <>
                    <Badge bg="whiteAlpha.200" color="white" fontSize="sm" px={3} py={1} borderRadius="full">
                      {invoices.length} Invoices
                    </Badge>
                    <Text fontSize="xs" opacity={0.6}>
                      {totalLineItems} draws total
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontSize="xs" opacity={0.6} textTransform="uppercase" letterSpacing="wider">
                      Invoice No.
                    </Text>
                    <Text fontSize="2xl" fontWeight="800" letterSpacing="1px">
                      {invoices[0]?.invoice_number || '\u2014'}
                    </Text>
                  </>
                )}
                <Text fontSize="xs" opacity={0.6}>
                  Issued {formatDate(invoices[0]?.created_at)}
                </Text>
              </VStack>
            </Flex>
          </Box>

          {/* Status Bar */}
          <Box
            bg={allPaid ? 'green.50' : 'orange.50'}
            px={{ base: 6, md: 10 }}
            py={3}
            borderBottom="1px solid"
            borderColor={allPaid ? 'green.100' : 'orange.100'}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Icon as={allPaid ? FaCheckCircle : FaCreditCard} color={allPaid ? 'green.500' : 'orange.500'} boxSize={4} />
                <Text fontSize="sm" fontWeight="bold" color={allPaid ? 'green.700' : 'orange.700'}>
                  {allPaid ? 'All Payments Received' : 'Payment Required'}
                </Text>
              </HStack>
              <Text fontSize="xl" fontWeight="800" color={allPaid ? 'green.700' : 'orange.700'}>
                {formatCurrency(grandTotal)}
              </Text>
            </Flex>
          </Box>

          {/* Bill To Section */}
          <Box px={{ base: 6, md: 10 }} py={6}>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>
                  Bill To
                </Text>
                <Text fontSize="lg" fontWeight="700" color="gray.800">
                  {labName}
                </Text>
                {labEmail && (
                  <Text fontSize="sm" color="gray.500" mt={0.5}>{labEmail}</Text>
                )}
              </Box>
              <Box textAlign="right">
                <Text fontSize="xs" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="wider" mb={1}>
                  Status
                </Text>
                <Badge
                  colorScheme={allPaid ? 'green' : 'red'}
                  fontSize="sm"
                  px={4}
                  py={1.5}
                  borderRadius="full"
                  fontWeight="700"
                >
                  {allPaid ? 'PAID' : 'UNPAID'}
                </Badge>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.100" />

          {/* Invoice Groups */}
          {invoices.map((inv, invIdx) => (
            <Box key={invIdx}>
              {isMultiple && (
                <Box
                  px={{ base: 6, md: 10 }}
                  py={4}
                  bg="gray.50"
                  borderBottom="1px solid"
                  borderColor="gray.100"
                  {...(invIdx > 0 ? { borderTop: '1px solid', borderTopColor: 'gray.200' } : {})}
                >
                  <Flex justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Box
                        bg="green.500"
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="md"
                        fontSize="xs"
                        fontWeight="800"
                      >
                        {inv.invoice_number || `INV-${invIdx + 1}`}
                      </Box>
                      {inv.batch_number && (
                        <Text fontSize="xs" color="gray.500">{inv.batch_number}</Text>
                      )}
                      <Badge
                        colorScheme={inv.payment_status === 'paid' ? 'green' : 'orange'}
                        fontSize="xs"
                        borderRadius="full"
                        px={2}
                      >
                        {(inv.payment_status || 'unpaid').toUpperCase()}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" fontWeight="700" color="gray.600">
                      {formatCurrency(inv.total_amount)}
                    </Text>
                  </Flex>
                </Box>
              )}

              {/* Line Items */}
              <Box px={{ base: 6, md: 10 }} py={5}>
                {invIdx === 0 && (
                  <Grid
                    templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                    gap={4}
                    pb={3}
                    mb={4}
                    borderBottom="2px solid"
                    borderColor="gray.200"
                  >
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Service Details
                    </Text>
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" display={{ base: 'none', md: 'block' }}>
                      Collection Date
                    </Text>
                    <Text fontSize="xs" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="wider" textAlign="right">
                      Amount
                    </Text>
                  </Grid>
                )}

                <VStack spacing={0} align="stretch" divider={<Divider borderColor="gray.100" />}>
                  {inv.line_items.map((item, i) => (
                    <Grid
                      key={i}
                      templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                      gap={4}
                      py={4}
                      _hover={{ bg: 'gray.50' }}
                      mx={-3}
                      px={3}
                      borderRadius="lg"
                      transition="background 0.15s"
                    >
                      <GridItem>
                        <HStack spacing={3} align="flex-start">
                          <Box
                            bg="green.50"
                            borderRadius="lg"
                            p={2}
                            mt={0.5}
                            flexShrink={0}
                          >
                            <Icon as={FaVial} color="green.400" boxSize={3.5} />
                          </Box>
                          <Box flex={1}>
                            <Text fontSize="sm" fontWeight="700" color="gray.800">
                              {item.patient_name}
                            </Text>
                            <HStack spacing={3} mt={1.5} flexWrap="wrap">
                              {item.patient_dob && (
                                <HStack spacing={1}>
                                  <Text fontSize="xs" color="gray.400">DOB:</Text>
                                  <Text fontSize="xs" color="gray.600" fontWeight="500">{formatDate(item.patient_dob)}</Text>
                                </HStack>
                              )}
                            </HStack>
                            {(item.doctor_name || item.phlebotomist_name) && (
                              <HStack spacing={4} mt={1.5} flexWrap="wrap">
                                {item.doctor_name && (
                                  <HStack spacing={1}>
                                    <Icon as={FaStethoscope} color="gray.300" boxSize={2.5} />
                                    <Text fontSize="xs" color="gray.500">{item.doctor_name}</Text>
                                  </HStack>
                                )}
                                {item.phlebotomist_name && (
                                  <HStack spacing={1}>
                                    <Icon as={FaSyringe} color="gray.300" boxSize={2.5} />
                                    <Text fontSize="xs" color="gray.500">{item.phlebotomist_name}</Text>
                                  </HStack>
                                )}
                              </HStack>
                            )}
                            {item.tests && item.tests.length > 0 && (
                              <HStack spacing={1.5} mt={2} flexWrap="wrap">
                                {item.tests.map((test, ti) => (
                                  <Badge
                                    key={ti}
                                    bg="green.50"
                                    color="green.600"
                                    fontSize="10px"
                                    fontWeight="600"
                                    px={2}
                                    py={0.5}
                                    borderRadius="full"
                                    border="1px solid"
                                    borderColor="green.100"
                                  >
                                    {test}
                                  </Badge>
                                ))}
                              </HStack>
                            )}
                            {item.diagnosis && (
                              <HStack spacing={1} mt={1}>
                                <Text fontSize="xs" color="gray.400" fontWeight="600">ICD:</Text>
                                <Text fontSize="xs" color="gray.500">{item.diagnosis}</Text>
                              </HStack>
                            )}
                            <Text fontSize="xs" color="gray.400" mt={1.5} display={{ base: 'block', md: 'none' }}>
                              {item.collection_date ? formatDate(item.collection_date) : '\u2014'}
                            </Text>
                          </Box>
                        </HStack>
                      </GridItem>
                      <GridItem display={{ base: 'none', md: 'flex' }} alignItems="flex-start" pt={1}>
                        <Text fontSize="sm" color="gray.600">
                          {item.collection_date ? formatDate(item.collection_date) : '\u2014'}
                        </Text>
                      </GridItem>
                      <GridItem textAlign="right" pt={1}>
                        <Text fontSize="sm" fontWeight="700" color="gray.800">
                          {formatCurrency(item.line_total)}
                        </Text>
                        {item.admin_fee > 0 && item.draw_fee > 0 && (
                          <Text fontSize="xs" color="gray.400" mt={0.5}>
                            {formatCurrency(item.draw_fee)} + {formatCurrency(item.admin_fee)}
                          </Text>
                        )}
                      </GridItem>
                    </Grid>
                  ))}

                  {inv.ship_fee > 0 && (
                    <Grid
                      templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                      gap={4}
                      py={3}
                    >
                      <GridItem>
                        <Text fontSize="sm" color="gray.600">Shipping Fee</Text>
                      </GridItem>
                      <GridItem display={{ base: 'none', md: 'block' }} />
                      <GridItem textAlign="right">
                        <Text fontSize="sm" fontWeight="600" color="gray.700">{formatCurrency(inv.ship_fee)}</Text>
                      </GridItem>
                    </Grid>
                  )}
                </VStack>

                {isMultiple && (
                  <Grid
                    templateColumns={{ base: '1fr auto', md: '1fr 140px 100px' }}
                    gap={4}
                    mt={3}
                    pt={3}
                    borderTop="2px solid"
                    borderColor="gray.200"
                  >
                    <GridItem>
                      <Text fontSize="sm" fontWeight="700" color="gray.600">
                        Subtotal — {inv.invoice_number || `Invoice ${invIdx + 1}`}
                      </Text>
                    </GridItem>
                    <GridItem display={{ base: 'none', md: 'block' }} />
                    <GridItem textAlign="right">
                      <Text fontSize="sm" fontWeight="800" color="gray.700">{formatCurrency(inv.total_amount)}</Text>
                    </GridItem>
                  </Grid>
                )}
              </Box>
            </Box>
          ))}

          {/* Grand Total */}
          <Box
            bg="gray.50"
            px={{ base: 6, md: 10 }}
            py={6}
            borderTop="2px solid"
            borderColor="gray.200"
          >
            <Flex justify="flex-end" align="center">
              <VStack align="end" spacing={1}>
                <HStack spacing={6}>
                  <Text fontSize="md" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    {isMultiple ? 'Grand Total' : 'Total Due'}
                  </Text>
                  <Text fontSize="3xl" fontWeight="800" color={allPaid ? 'green.600' : 'gray.800'}>
                    {formatCurrency(grandTotal)}
                  </Text>
                </HStack>
                {isMultiple && (
                  <Text fontSize="xs" color="gray.400">
                    {invoices.length} invoices &middot; {totalLineItems} blood draws
                  </Text>
                )}
              </VStack>
            </Flex>
          </Box>

          {/* Payment Section */}
          <Box px={{ base: 6, md: 10 }} py={8} bg="white">
            {allPaid ? (
              <Box
                bg="green.50"
                borderRadius="xl"
                p={6}
                textAlign="center"
                border="1px solid"
                borderColor="green.100"
              >
                <Icon as={FaCheckCircle} color="green.400" boxSize={8} mb={3} />
                <Text fontWeight="700" color="green.700" fontSize="lg">Payment Complete</Text>
                <Text fontSize="sm" color="green.600" mt={1}>
                  Thank you for your payment. No further action is needed.
                </Text>
              </Box>
            ) : (
              <VStack spacing={5} align="stretch">
                <Button
                  bgGradient="linear(to-r, #276749, #38a169)"
                  color="white"
                  size="lg"
                  width="full"
                  leftIcon={<Icon as={FaCreditCard} />}
                  fontSize="md"
                  py={7}
                  borderRadius="xl"
                  _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                  _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                  transition="all 0.2s"
                  boxShadow="md"
                  onClick={onOpen}
                >
                  Pay {formatCurrency(grandTotal)}
                </Button>
                <HStack spacing={1.5} justify="center">
                  <Icon as={FaLock} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.400">
                    Secure payment powered by Stripe
                  </Text>
                </HStack>
              </VStack>
            )}
          </Box>

          {/* Payment Modal */}
          <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
              <Box bgGradient="linear(to-r, #276749, #38a169)" px={6} py={4}>
                <Flex justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <Text color="white" fontSize="lg" fontWeight="700">Payment</Text>
                    <Text color="whiteAlpha.800" fontSize="sm">{formatCurrency(grandTotal)} due</Text>
                  </VStack>
                  <Box
                    as="button"
                    onClick={onClose}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    p={1}
                  >
                    <Icon as={FaTimes} boxSize={4} />
                  </Box>
                </Flex>
              </Box>

              <ModalBody px={6} py={5}>
                <VStack spacing={4} align="stretch">
                  {/* Tabs: Card / Bank */}
                  <HStack spacing={0}>
                    <Box
                      flex={1}
                      py={2.5}
                      textAlign="center"
                      borderBottom="2px solid"
                      borderColor={paymentTab === 'card' ? 'green.500' : 'gray.200'}
                      cursor="pointer"
                      onClick={() => setPaymentTab('card')}
                      transition="all 0.2s"
                    >
                      <HStack justify="center" spacing={2}>
                        <Icon as={FaRegCreditCard} color={paymentTab === 'card' ? 'green.600' : 'gray.400'} boxSize={3.5} />
                        <Text fontSize="sm" fontWeight="600" color={paymentTab === 'card' ? 'green.700' : 'gray.400'}>Card</Text>
                      </HStack>
                    </Box>
                    <Box
                      flex={1}
                      py={2.5}
                      textAlign="center"
                      borderBottom="2px solid"
                      borderColor={paymentTab === 'bank' ? 'green.500' : 'gray.200'}
                      cursor="pointer"
                      onClick={() => setPaymentTab('bank')}
                      transition="all 0.2s"
                    >
                      <Text fontSize="sm" fontWeight={paymentTab === 'bank' ? '600' : '500'} color={paymentTab === 'bank' ? 'green.700' : 'gray.400'}>US bank account</Text>
                    </Box>
                  </HStack>

                  {paymentTab === 'card' ? (
                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Card number</Text>
                      <Box bg="white" border="1px solid" borderColor="gray.300" borderRadius="md">
                        <HStack spacing={0}>
                          <input
                            type="text"
                            placeholder="1234 1234 1234 1234"
                            style={{
                              flex: 1,
                              border: 'none',
                              outline: 'none',
                              background: 'transparent',
                              padding: '10px 12px',
                              fontSize: '14px',
                              color: '#2D3748',
                              fontFamily: 'inherit',
                            }}
                          />
                          <HStack spacing={1} pr={3}>
                            <Box w="28px" h="18px" bg="blue.600" borderRadius="3px" />
                            <Box w="28px" h="18px" bg="red.500" borderRadius="3px" opacity={0.4} />
                            <Box w="28px" h="18px" bg="orange.400" borderRadius="3px" opacity={0.4} />
                          </HStack>
                        </HStack>
                      </Box>
                    </Box>

                    <HStack spacing={3}>
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Expiration date</Text>
                        <input type="text" placeholder="MM / YY" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                      </Box>
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>CVC</Text>
                        <input type="text" placeholder="CVC" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                      </Box>
                    </HStack>

                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Country</Text>
                      <select defaultValue="US" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                      </select>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>ZIP</Text>
                      <input type="text" placeholder="12345" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                    </Box>
                  </VStack>
                  ) : (
                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Routing number</Text>
                      <input type="text" placeholder="110000000" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Account number</Text>
                      <input type="text" placeholder="000123456789" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Confirm account number</Text>
                      <input type="text" placeholder="000123456789" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Account holder name</Text>
                      <input type="text" placeholder="Full name on the account" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none' }} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1.5}>Account type</Text>
                      <select defaultValue="checking" style={{ width: '100%', border: '1px solid #CBD5E0', borderRadius: '6px', background: 'white', padding: '10px 12px', fontSize: '14px', color: '#2D3748', fontFamily: 'inherit', outline: 'none', cursor: 'pointer' }}>
                        <option value="checking">Checking</option>
                        <option value="savings">Savings</option>
                      </select>
                    </Box>
                  </VStack>
                  )}

                  <Button
                    bgGradient="linear(to-r, #276749, #38a169)"
                    color="white"
                    size="lg"
                    width="full"
                    leftIcon={<Icon as={FaCreditCard} />}
                    fontSize="md"
                    py={7}
                    borderRadius="xl"
                    _hover={{ bgGradient: 'linear(to-r, #38a169, #48bb78)', transform: 'translateY(-1px)', boxShadow: 'lg' }}
                    _active={{ transform: 'translateY(0)', boxShadow: 'md' }}
                    transition="all 0.2s"
                    boxShadow="md"
                  >
                    Pay {formatCurrency(grandTotal)}
                  </Button>

                  <HStack spacing={1.5} justify="center" pb={2}>
                    <Icon as={FaLock} color="gray.400" boxSize={3} />
                    <Text fontSize="xs" color="gray.400">
                      Secure payment powered by Stripe
                    </Text>
                  </HStack>
                </VStack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Footer */}
          <Box
            bg="gray.50"
            px={{ base: 6, md: 10 }}
            py={5}
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <Text fontSize="xs" color="gray.500" fontWeight="600">
                AtHomeBloodDraw.com
              </Text>
              <Text fontSize="xs" color="gray.400">
                Questions? Contact support@athomeblooddraw.com
              </Text>
            </Flex>
          </Box>
        </Box>

        <Text textAlign="center" fontSize="xs" color="gray.400" mt={6}>
          Mobile Phlebotomy Services
        </Text>
      </Container>
    </Box>
  );
}
